import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as ostacoliApi from "api/ostacoli/ostacoliApi";
import {toast} from "react-toastify";
import {FiltriOstacoli} from "components/userPreference/userPreferenceSlice";
import {getOdPLegacy} from "components/main/produzione/odpLegacySlice";
import React from "react";
import {SvgIconProps} from "@mui/material/SvgIcon";
import ConstructionIcon from "@mui/icons-material/Construction";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FactoryIcon from "@mui/icons-material/Factory";
import EventNoteIcon from "@mui/icons-material/EventNote";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";

// ============================================
// COSTANTI
// ============================================

// Mappa tipo ostacolo → icona
export const OSTACOLO_ICON_MAP: Record<string, React.ComponentType<SvgIconProps>> = {
    MAT: LocalShippingIcon,
    ATT: ConstructionIcon,
    QUA: ReportProblemIcon,
    PER: PersonOffIcon,
    TEC: SettingsIcon,
    EST: FactoryIcon,
    NSC: EventNoteIcon,
    NPR: StickyNote2Icon,
    ALT: HelpOutlineIcon,
};

export const TIPI_OSTACOLO = [
    {value: "MAT", label: "Materiale mancante"},
    {value: "ATT", label: "Attrezzatura non disponibile"},
    {value: "QUA", label: "Problema qualità"},
    {value: "PER", label: "Personale non disponibile"},
    {value: "TEC", label: "Problema tecnico"},
    {value: "EST", label: "Attesa fornitore/terzista"},
    {value: "NSC", label: "Note schedulazione"},
    {value: "NPR", label: "Note produzione"},
    {value: "ALT", label: "Altro"},
] as const;

// ============================================
// INTERFACES
// ============================================

export interface Ostacolo {
    id?: number;
    odp?: number; // FK OdPLegacy
    odp_display?: string; // display dal backend
    codice_articolo?: string; // display dal backend
    nome_cliente?: string; // display dal backend
    tipo?: string;
    tipo_display?: string; // display dal backend
    descrizione?: string;
    data_segnalazione?: string; // read-only dal backend
    data_prevista_risoluzione?: string| null;
    data_risoluzione?: string | null;
    risolto?: boolean;
    note_risoluzione?: string;
    is_scaduto?: boolean; // read-only dal backend
    giorni_ritardo?: number; // read-only dal backend
    errorsStack?: ErrorsStack;
}

export interface OstacoliList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Ostacolo[];
    errorsStack: ErrorsStack;
}

// ============================================
// STATO SLICE
// ============================================

export interface OstacoliState {
    ostacoliList: OstacoliList;
    ostacoloCorrenteId?: number; // SOLO ID, mai oggetto completo
    // NO filtri qui! Sono in userPreferenceSlice
}

const initialState: OstacoliState = {
    ostacoliList: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    ostacoloCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchOstacoli = createAsyncThunk(
    "ostacoli/fetchOstacoli",
    async (parametri: FiltriOstacoli = {}) => {
        return await ostacoliApi.fetchOstacoli(parametri);
    }
);

export const getOstacolo = createAsyncThunk(
    "ostacoli/getOstacolo",
    async (parametri: { ostacoloId: number }) => {
        return await ostacoliApi.getOstacolo(parametri.ostacoloId);
    }
);

export const saveOstacolo = createAsyncThunk(
    "ostacoli/saveOstacolo",
    async (parametri: { ostacoloToSave: Ostacolo }, thunkAPI) => {
        const result = await ostacoliApi.saveOstacolo(parametri.ostacoloToSave);
        // Aggiorna l'OdP padre per riflettere gli ostacoli aggiornati
        if (result.odp) {
            thunkAPI.dispatch(getOdPLegacy({odpLegacyId: result.odp}));
        }
        return result;
    }
);

export const deleteOstacolo = createAsyncThunk(
    "ostacoli/deleteOstacolo",
    async (parametri: { ostacoloToDelete: Ostacolo }, thunkAPI) => {
        const result = await ostacoliApi.deleteOstacolo(parametri.ostacoloToDelete);
        // Aggiorna l'OdP padre per riflettere gli ostacoli aggiornati
        if (parametri.ostacoloToDelete.odp) {
            thunkAPI.dispatch(getOdPLegacy({odpLegacyId: parametri.ostacoloToDelete.odp}));
        }
        return result;
    }
);

// ============================================
// SLICE
// ============================================

export const ostacoliSlice = createSlice({
    name: "ostacoli",
    initialState,
    reducers: {
        setOstacoloCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.ostacoloCorrenteId = action.payload;
        },
        resetOstacoli: (state) => {
            state.ostacoloCorrenteId = undefined;
            state.ostacoliList = initialState.ostacoliList;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH (Lista)
        // ============================================
        builder.addCase(fetchOstacoli.pending, (state) => {
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(fetchOstacoli.fulfilled, (state, action) => {
            state.ostacoliList = action.payload;
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(fetchOstacoli.rejected, (state, action) => {
            state.ostacoliList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // GET (Singolo)
        // ============================================
        builder.addCase(getOstacolo.pending, (state) => {
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(getOstacolo.fulfilled, (state, action) => {
            state.ostacoliList.results = state.ostacoliList.results.filter(
                (ostacolo) => ostacolo.id !== action.payload.id
            );
            state.ostacoliList.results.push(action.payload);
            state.ostacoliList.results.sort((a, b) => (a.id || 0) - (b.id || 0));
            state.ostacoloCorrenteId = action.payload.id;
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(getOstacolo.rejected, (state, action) => {
            state.ostacoliList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // SAVE (Create/Update) - NO TOAST, usa AutoSaveIndicator
        // ============================================
        builder.addCase(saveOstacolo.pending, (state) => {
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(saveOstacolo.fulfilled, (state, action) => {
            const index = state.ostacoliList.results.findIndex(
                (ostacolo) => ostacolo.id === action.payload.id
            );

            if (index !== -1) {
                // Update esistente
                state.ostacoliList.results[index] = {
                    ...action.payload,
                    errorsStack: {status: ErrorStatusTypes.SUCCESS},
                };
            } else {
                // Nuovo ostacolo
                state.ostacoliList.results.unshift({
                    ...action.payload,
                    errorsStack: {status: ErrorStatusTypes.SUCCESS},
                });
                state.ostacoliList.count++;
            }

            state.ostacoliList.results.sort((a, b) => (a.id || 0) - (b.id || 0));
            state.ostacoloCorrenteId = action.payload.id;
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.SUCCESS};
        });
        builder.addCase(saveOstacolo.rejected, (state, action) => {
            state.ostacoliList.results = state.ostacoliList.results.map((ostacolo) => {
                if (ostacolo.id === action.meta.arg.ostacoloToSave.id) {
                    return {
                        ...ostacolo,
                        errorsStack: parseErrorMessage(action.error),
                    };
                }
                return ostacolo;
            });
            state.ostacoliList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // DELETE
        // ============================================
        builder.addCase(deleteOstacolo.pending, (state) => {
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(deleteOstacolo.fulfilled, (state, action) => {
            state.ostacoliList.results = state.ostacoliList.results.filter(
                (ostacolo) => ostacolo.id !== action.meta.arg.ostacoloToDelete.id
            );
            state.ostacoliList.count--;
            state.ostacoliList.errorsStack = {status: ErrorStatusTypes.OK};

            toast.success("Ostacolo eliminato con successo");
        });
        builder.addCase(deleteOstacolo.rejected, (state, action) => {
            state.ostacoliList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione dell'ostacolo");
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setOstacoloCorrenteId,
    resetOstacoli,
} = ostacoliSlice.actions;

export const ostacoliSliceReducer = ostacoliSlice.reducer;
